import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import Image from '../../components/images/Image';
import Center from '../../components/css/Center';
import Left from '../../components/css/Left';
import InputField from '../../components/forms/InputField';
import TextArea from '../../components/forms/TextArea';
import ReCaptchaWidget from '../../components/forms/ReCaptchaWidget';
import NotFound from '../../components/common/NotFound';
import Loading from '../../components/common/Loading';
import EventCta from '../../components/common/EventCta';
import { teamPhoto } from '../../utils';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Build-time fallback for legacy deployments — the team payload now ships
// the server-configured site key so the widget render and backend secret
// can't drift apart. See /api/public-team/<id>.recaptcha_site_key.
const BUILD_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const fetchPublicTeam = async (teamId) => {
  const response = await fetch(`/api/public-team/${encodeURIComponent(teamId)}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    let errorMessage = 'somethingWentWrong';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // Keep the fallback message.
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

const submitVote = async (teamId, payload) => {
  const response = await fetch(`/api/public-team/${encodeURIComponent(teamId)}/vote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  let body = {};
  try { body = await response.json(); } catch { /* empty body is fine */ }

  return { ok: response.ok, status: response.status, body };
};

const Page = () => {
  const { teamId } = useParams();
  const { t } = useTranslation('common');
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedState, setSubmittedState] = useState(null); // 'counted' | 'already_counted'

  useEffect(() => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setComment('');
    setCaptchaToken('');
    setFormError(null);
    setSubmittedState(null);
  }, [teamId]);

  useEffect(() => {
    let ignore = false;

    const loadTeam = async () => {
      if (!teamId) {
        setLoading(false);
        setError('teamNotFound');
        return;
      }

      try {
        setLoading(true);
        const response = await fetchPublicTeam(teamId);
        if (!ignore) {
          setTeamData(response);
          setError(null);
        }
      } catch (err) {
        if (!ignore) {
          setTeamData(null);
          setError(err.message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadTeam();
    return () => { ignore = true; };
  }, [teamId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedFirstName = firstName.trim();
    const normalizedLastName  = lastName.trim();
    const normalizedEmail     = email.trim().toLowerCase();
    const normalizedComment   = comment.trim();

    if (!normalizedFirstName) {
      setFormError('First name is required.');
      return;
    }

    if (!normalizedLastName) {
      setFormError('Last name is required.');
      return;
    }

    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setFormError('Please enter a valid email address.');
      return;
    }

    if (!normalizedComment) {
      setFormError('Please write a short message.');
      return;
    }

    if (recaptchaSiteKey && !captchaToken) {
      setFormError(t('completeCaptcha'));
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);

      const { ok, body } = await submitVote(teamId, {
        first_name:      normalizedFirstName,
        last_name:       normalizedLastName,
        email:           normalizedEmail,
        comment:         normalizedComment,
        recaptcha_token: captchaToken,
      });

      if (!ok) {
        setFormError(t(body.message || 'somethingWentWrong'));
        // reCAPTCHA tokens are single-use; force a fresh challenge on retry.
        setCaptchaToken('');
        return;
      }

      if (body.message === 'voteAlreadyCounted') {
        setSubmittedState('already_counted');
      } else {
        setSubmittedState('counted');
      }
    } catch (err) {
      setFormError(t('somethingWentWrong'));
      setCaptchaToken('');
    } finally {
      setSubmitting(false);
    }
  };

  const team = teamData?.team;
  const teamImage = teamPhoto(team?.picture, team?.company_picture);
  const useContainedTeamImage = !team?.picture && Boolean(team?.company_picture);

  // Prefer the site key the backend ships next to the team payload so the
  // widget and backend secret stay in sync. Fall back to the build-time
  // env var only when the API doesn't expose the field yet.
  const recaptchaSiteKey = teamData?.recaptcha_site_key || BUILD_SITE_KEY;

  return (
    <Screen color="#f3f4f6" p="3">
      <div className="top">
        <div className="public-page-shell">
          {loading ? (
            <Card p="3">
              <Loading size="lg" />
            </Card>
          ) : !team ? (
            <NotFound translationKey="publicTeamNotFound" loading={false} />
          ) : (
            <>
              <Card p="3">
                <Left>
                  <Button
                    to={`/public-team/${teamId}`}
                    theme="light"
                    className="mb-3 px-2"
                    styles={{ width: 'auto' }}
                  >
                    {t('backToTeam')}
                  </Button>
                </Left>

                <Center>
                  <Image
                    className={`mb-3 ${useContainedTeamImage ? '' : 'circle'}`}
                    url={teamImage}
                    size="6.5rem"
                    contain={useContainedTeamImage}
                    reload
                  />
                </Center>

                <Title heading="3" my="0">{t('voteForTeam', { team: team.name })}</Title>
                <p className="text-secondary mb-0">{t('publicVoteIntro')}</p>
              </Card>

              {submittedState ? (
                <>
                  <Card p="3">
                    {submittedState === 'already_counted' ? (
                      <>
                        <Title heading="4" my="1">Vote already counted</Title>
                        <p className="text-secondary">
                          You've already voted for this team — thanks for the support!
                        </p>
                      </>
                    ) : (
                      <>
                        <Title heading="4" my="1">Thanks for voting!</Title>
                        <p className="text-secondary">
                          Your vote for <strong>{team.name}</strong> has been counted.
                        </p>
                      </>
                    )}
                    <Button to={`/public-team/${teamId}`}>{t('returnToTeam')}</Button>
                  </Card>

                  <EventCta eventName={team.event_name} eventId={team.event_id} referralLink={teamData?.referral_link} commercialUrl={teamData?.commercial_url} />
                </>
              ) : (
                <>
                  <Card p="3">
                    <div className="public-vote-total mb-3 text-center">
                      <div className="public-support-note text-uppercase">{t('publicVoteCount')}</div>
                      <Title heading="2" my="1" className="mb-1">{teamData?.total_votes ?? 0}</Title>
                    </div>

                    <form onSubmit={handleSubmit}>
                      <div className="row g-2">
                        <div className="col-md-6">
                          <InputField
                            type="text"
                            name="firstName"
                            label="First name"
                            placeholder=""
                            value={firstName}
                            onChange={(event) => {
                              setFirstName(event.target.value);
                              if (formError) setFormError(null);
                            }}
                            required
                            maxLength={40}
                          />
                        </div>
                        <div className="col-md-6">
                          <InputField
                            type="text"
                            name="lastName"
                            label="Last name"
                            placeholder=""
                            value={lastName}
                            onChange={(event) => {
                              setLastName(event.target.value);
                              if (formError) setFormError(null);
                            }}
                            required
                            maxLength={50}
                          />
                        </div>
                      </div>

                      <InputField
                        type="email"
                        name="email"
                        label="Your email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          if (formError) setFormError(null);
                        }}
                        required
                        maxLength={120}
                      />

                      <TextArea
                        name="supportComment"
                        label={t('supportMessage')}
                        placeholder={t('writeSupportMessage')}
                        rows={4}
                        value={comment}
                        onChange={(event) => {
                          setComment(event.target.value);
                          if (formError) setFormError(null);
                        }}
                      />

                      {recaptchaSiteKey && (
                        <div className="mb-3">
                          <ReCaptchaWidget
                            siteKey={recaptchaSiteKey}
                            onVerify={(token) => {
                              setCaptchaToken(token);
                              if (formError) setFormError(null);
                            }}
                            onExpire={() => setCaptchaToken('')}
                            onError={() => setCaptchaToken('')}
                          />
                        </div>
                      )}

                      {formError && (
                        <p className="text-danger text-start mb-3">{formError}</p>
                      )}

                      <Button
                        type="submit"
                        disabled={submitting || (recaptchaSiteKey && !captchaToken)}
                      >
                        {submitting ? t('pleaseWait') : t('submit')}
                      </Button>
                    </form>
                  </Card>

                  <EventCta eventName={team.event_name} eventId={team.event_id} referralLink={teamData?.referral_link} commercialUrl={teamData?.commercial_url} />
                </>
              )}
            </>
          )}

          {!loading && error && !team && (
            <p className="text-secondary text-center mt-3 mb-0">
              {t(error, { defaultValue: t('publicTeamNotFound') })}
            </p>
          )}
        </div>
      </div>
    </Screen>
  );
};

export default Page;
